/**
 * the document *project*: a named, ordered subset of the PDFs in one
 * request whose redacted outputs concatenate into a single deliverable.
 */
import { TDOId } from '@common-modules/universal/models/Brands';

/**
 * Bumped when the stored shape changes in a way a reader has to branch on.
 * Records carrying a FUTURE version are rejected by the parsers rather than
 * read optimistically: an older client guessing at a newer shape is how a
 * project loses members on its next save.
 */
export const PROJECT_SCHEMA_VERSION = 1;

export const PROJECT_KINDS = ['document'] as const;

/**
 * What a project combines. Only `document` is produced today; `video`, `audio`
 * and `image` are the reserved future values that make this an enum rather than
 * a boolean.
 */
export type ProjectKind = (typeof PROJECT_KINDS)[number];

export const isProjectKind = (value: unknown): value is ProjectKind =>
  typeof value === 'string' &&
  (PROJECT_KINDS as readonly string[]).includes(value);

/** One member of a project, in its display/concatenation position. */
export interface ProjectMember {
  readonly tdoId: TDOId;
}

/**
 * Cached facts about one member, keyed by TDO in `memberInfo`.
 *
 * A CACHE. The member's own TDO stays authoritative — a reader holding the
 * file uses the file, and only one that does not falls back here. What makes
 * that safe is that the fact cached is immutable: `originalPageCount` is the
 * count the document was ingested with, never the redacted output's
 * `numPages`, so deleted pages still consume their numbers (VE-30106).
 */
export interface ProjectMemberInfo {
  /** Absent when it was not known at the time membership was written. */
  readonly originalPageCount?: number;
}

/**
 * `details.project` on the project TDO — the membership, which is the project.
 */
export interface ProjectDetails {
  readonly version: number;
  readonly kind: ProjectKind;
  readonly members: readonly ProjectMember[];
  /**
   * Per-member facts, BESIDE the order rather than inside it.
   *
   * A sibling map rather than fields on each member, because `withMembers`
   * rebuilds the member list from a plain array of ids on every add, remove
   * AND reorder — so anything stored inline would be silently discarded the
   * next time somebody dragged a row. Keyed by TDO, it survives untouched and
   * a reorder stays a one-line change.
   *
   * Written so that a reader holding the project does not have to load every
   * member's TDO to work out the numbering: order lives here, and the page
   * counts the sequence spans can live here too.
   *
   * WHICH MAP A NEW FIELD BELONGS IN. This one holds facts about the FILE,
   * cached because the project would otherwise have to load every member's
   * TDO to learn them — `originalPageCount` is true of the document whether
   * or not it is in a merge. A fact about the MEMBERSHIP, true only because
   * the file is part of this project — whether it is active, say, or
   * complete — belongs inline on the member in `members`, beside the order
   * that is the other such fact.
   *
   * Note that inline is not free today: `withMembers` rebuilds the list from
   * plain ids, so a field put there would be discarded on the next reorder.
   * Adding one means teaching that writer to carry it through, the way this
   * map is carried through.
   */
  readonly memberInfo: Readonly<Record<string, ProjectMemberInfo>>;
  readonly createdDateTime: string;
  readonly modifiedDateTime: string;
  readonly createdBy?: string;
}

/**
 * How the merged document's filename is assembled — the two parts a reviewer
 * can turn off, stored so the choice survives a reload.
 */
export interface MergedFilenameOptions {
  /** Include the `[MERGED]` tag. Named for the choice, not for the mechanism. */
  readonly appendMerged: boolean;
  readonly includeDate: boolean;
}

/** Both on — what a merged document is called when nobody has said otherwise. */
export const DEFAULT_MERGED_FILENAME_OPTIONS: MergedFilenameOptions = {
  appendMerged: true,
  includeDate: true,
};

/**
 * Read the options off a project TDO's `details`.
 */
export const parseMergedFilenameOptions = (
  details: unknown
): MergedFilenameOptions => {
  if (!isRecord(details)) {
    return DEFAULT_MERGED_FILENAME_OPTIONS;
  }
  const raw = details.mergedFilename;
  if (!isRecord(raw)) {
    return DEFAULT_MERGED_FILENAME_OPTIONS;
  }
  return {
    appendMerged:
      typeof raw.appendMerged === 'boolean'
        ? raw.appendMerged
        : DEFAULT_MERGED_FILENAME_OPTIONS.appendMerged,
    includeDate:
      typeof raw.includeDate === 'boolean'
        ? raw.includeDate
        : DEFAULT_MERGED_FILENAME_OPTIONS.includeDate,
  };
};

/**
 * One entry in the request folder's `projects` content template — enough to
 * find and label a project without opening its TDO.
 */
export interface ProjectPointer {
  readonly tdoId: TDOId;
  readonly kind: ProjectKind;
  readonly name?: string;
  readonly createdDateTime?: string;
  readonly modifiedDateTime?: string;
}

/** The content template's `data` payload. */
export interface ProjectsTemplateData {
  readonly projects: readonly ProjectPointer[];
}

/**
 * The slice of a TDO's `details` this module cares about. Deliberately NOT the
 * full details type — every other field on there belongs to someone else.
 */
export interface ProjectTdoDetails {
  readonly isProject?: boolean;
  readonly project?: unknown;
  /**
   * The project's own monotonic combined-output counter, mirrored off the
   * assets the way `latestRedactedDocumentVersion` is for a single file: once
   * old combined assets rotate away, the assets can no longer say what the next
   * version number is.
   */
  readonly latestCombinedVersion?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined;

/**
 * Is this TDO a project rather than a source file?
 */
export const isProjectTdo = (details: unknown): boolean =>
  isRecord(details) && details.isProject === true;

/**
 * Parse one member. Anything that isn't a usable TDO id is dropped by the
 * caller rather than preserved as a hole — a null in the member list would
 * reach the combine as a missing document with no name to report.
 */
const parseMember = (value: unknown): ProjectMember | null => {
  if (typeof value === 'string') {
    // Tolerated because it is the obvious thing for a hand-written record or a
    // future writer to produce, and the meaning is unambiguous.
    const tdoId = asNonEmptyString(value);
    return tdoId ? { tdoId: tdoId as TDOId } : null;
  }
  if (!isRecord(value)) {
    return null;
  }
  const tdoId = asNonEmptyString(value.tdoId);
  return tdoId ? { tdoId: tdoId as TDOId } : null;
};

/**
 * Parse the ordered member list, dropping unusable entries and de-duplicating
 * by TDO while KEEPING FIRST POSITION.
 */
const parseMembers = (value: unknown): readonly ProjectMember[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  const members: ProjectMember[] = [];
  for (const entry of value) {
    const member = parseMember(entry);
    if (!member || seen.has(member.tdoId)) {
      continue;
    }
    seen.add(member.tdoId);
    members.push(member);
  }
  return members;
};

/**
 * Parse the per-member cache, keeping only entries that say something usable.
 *
 * An entry for a TDO that is no longer a member is dropped by `withMembers`
 * on the next write rather than here: this reads what is stored, and a
 * reader asking about a non-member simply gets nothing.
 */
const parseMemberInfo = (value: unknown): Record<string, ProjectMemberInfo> => {
  if (!isRecord(value)) {
    return {};
  }
  const info: Record<string, ProjectMemberInfo> = {};
  for (const [tdoId, raw] of Object.entries(value)) {
    if (!asNonEmptyString(tdoId) || !isRecord(raw)) {
      continue;
    }
    const count = raw.originalPageCount;
    // A positive integer or nothing. Zero and negatives are not a page count
    // and would corrupt the numbering silently; absence is handled already.
    if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
      info[tdoId] = { originalPageCount: Math.floor(count) };
    }
  }
  return info;
};

/**
 * Read `details.project` off a project TDO, or null if this TDO carries no
 * usable project.
 */
export const parseProjectDetails = (
  details: unknown
): ProjectDetails | null => {
  if (!isRecord(details) || details.isProject !== true) {
    return null;
  }
  const project = details.project;
  if (!isRecord(project)) {
    return null;
  }
  const version =
    typeof project.version === 'number' && Number.isFinite(project.version)
      ? project.version
      : PROJECT_SCHEMA_VERSION;
  if (version > PROJECT_SCHEMA_VERSION) {
    return null;
  }
  if (!isProjectKind(project.kind)) {
    return null;
  }
  const createdDateTime = asNonEmptyString(project.createdDateTime) ?? '';
  return {
    version,
    kind: project.kind,
    members: parseMembers(project.members),
    memberInfo: parseMemberInfo(project.memberInfo),
    createdDateTime,
    modifiedDateTime:
      asNonEmptyString(project.modifiedDateTime) ?? createdDateTime,
    createdBy: asNonEmptyString(project.createdBy),
  };
};

/** The combined-output version counter on a project TDO, or 0 if none yet. */
export const parseLatestCombinedVersion = (details: unknown): number => {
  if (!isRecord(details)) {
    return 0;
  }
  const value = details.latestCombinedVersion;
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
};

/** Parse one pointer from the request folder's content template. */
const parsePointer = (value: unknown): ProjectPointer | null => {
  if (!isRecord(value)) {
    return null;
  }
  const tdoId = asNonEmptyString(value.tdoId);
  if (!tdoId || !isProjectKind(value.kind)) {
    return null;
  }
  return {
    tdoId: tdoId as TDOId,
    kind: value.kind,
    name: asNonEmptyString(value.name),
    createdDateTime: asNonEmptyString(value.createdDateTime),
    modifiedDateTime: asNonEmptyString(value.modifiedDateTime),
  };
};

/**
 * Read the `projects` array out of a content template's `data`.
 */
export const parseProjectPointers = (
  data: unknown
): readonly ProjectPointer[] => {
  if (!isRecord(data) || !Array.isArray(data.projects)) {
    return [];
  }
  const seen = new Set<string>();
  const pointers: ProjectPointer[] = [];
  for (const entry of data.projects) {
    const pointer = parsePointer(entry);
    if (!pointer || seen.has(pointer.tdoId)) {
      continue;
    }
    seen.add(pointer.tdoId);
    pointers.push(pointer);
  }
  return pointers;
};

/**
 * Build the `details` for a new project TDO.
 */
export const buildNewProjectDetails = (args: {
  readonly kind: ProjectKind;
  readonly members: readonly TDOId[];
  readonly now: string;
  readonly createdBy?: string;
  /**
   * A member's ORIGINAL page count, as `withMembers` takes it.
   *
   * A project IS created with members — setup picks them — so the counts are
   * knowable at creation and the cache starts full. It used to start empty on
   * the theory that nothing had asked yet; the result was that a set nobody
   * had since edited had no spans at all, and every reader that falls back to
   * this cache answered "cannot say" for the whole release.
   */
  readonly pageCountOf?: (tdoId: TDOId) => number | null;
}): { isProject: true; project: ProjectDetails } => ({
  isProject: true,
  project: {
    version: PROJECT_SCHEMA_VERSION,
    kind: args.kind,
    members: parseMembers(args.members),
    memberInfo: buildMemberInfo(parseMembers(args.members), args.pageCountOf),
    createdDateTime: args.now,
    modifiedDateTime: args.now,
    ...(args.createdBy ? { createdBy: args.createdBy } : {}),
  },
});

/**
 * The page-count cache for a membership, built from one pass over it.
 *
 * SHARED BY CREATION AND EDIT so the two cannot disagree about what counts as
 * cacheable — they did, because creation had no cache at all. Only current
 * members get an entry, so a removed one leaves nothing stale behind, and a
 * count that is absent or nonsense is simply not cached: `null` and a missing
 * key read the same downstream, and a zero or negative span would corrupt
 * every start after it in the sequence.
 */
const buildMemberInfo = (
  members: readonly ProjectMember[],
  pageCountOf?: (tdoId: TDOId) => number | null,
  /** Where an edit's existing cache carries through; creation has none. */
  existing?: Readonly<Record<string, ProjectMemberInfo>>
): Record<string, ProjectMemberInfo> => {
  const memberInfo: Record<string, ProjectMemberInfo> = {};
  for (const member of members) {
    const count =
      pageCountOf?.(member.tdoId) ??
      existing?.[member.tdoId]?.originalPageCount ??
      null;
    if (typeof count === 'number' && count > 0) {
      memberInfo[member.tdoId] = { originalPageCount: Math.floor(count) };
    }
  }
  return memberInfo;
};

/**
 * Apply a new ordered membership to an existing project, returning the details
 * to persist. The created-at and the author are carried through — an edit is
 * not a re-creation.
 */
export const withMembers = (
  project: ProjectDetails,
  members: readonly TDOId[],
  now: string,
  /**
   * A member's ORIGINAL page count, for refreshing the cache. Optional: a
   * caller that does not have the counts to hand leaves what is stored
   * alone rather than erasing it.
   */
  pageCountOf?: (tdoId: TDOId) => number | null
): ProjectDetails => {
  const next = parseMembers(members);
  /**
   * REBUILT FROM THE NEW LIST, which prunes and refreshes in one pass.
   *
   * A fresh count wins where the caller has one; otherwise the stored value
   * carries through, so a reorder by a caller with no counts to hand keeps
   * everything it already knew.
   */
  return {
    ...project,
    members: next,
    memberInfo: buildMemberInfo(next, pageCountOf, project.memberInfo),
    modifiedDateTime: now,
  };
};

/**
 * A member's original page count as the PROJECT has it, or null.
 *
 * The fallback, not the source: a caller holding the member's own TDO should
 * read `details.veritoneFile.pageCount` off that instead, because the file is
 * authoritative and this is a cache of it.
 */
export const projectMemberPageCount = (
  project: ProjectDetails | null | undefined,
  tdoId: TDOId | string
): number | null =>
  project?.memberInfo?.[String(tdoId)]?.originalPageCount ?? null;
